import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useContext } from 'react';

import { AppTheme } from 'theme';
import { layout, LayoutProps, space, SpaceProps } from 'styled-system';

import Tooltip from 'components/Tooltip';
import shouldForwardProp from 'utils/shouldForwardProp';

import tooltipImage from '../assets/lock.svg';
import colors from '../lib/colors';

export type LabelT = { key: string; defaultValue?: string };

export type LabelOrLabelT =
  | { label: string | React.ReactNode; labelT?: undefined }
  | { label?: undefined; labelT: LabelT };
interface Props extends LayoutProps, SpaceProps {
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disabled?: React.ButtonHTMLAttributes<HTMLButtonElement>['disabled'];
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  narrow?: boolean;
  short?: boolean;
  extraWide?: boolean;
  focus?: boolean;
  tooltip?: string | null;
  variant?:
  | 'default'
  | 'primary'
  | 'primary-text'
  | 'danger'
  | 'warning'
  | 'warningFaded'
  | 'outline-default'
  | 'outline-primary'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-warningFaded'
  | 'transparent'
  | 'transparent70';
  css?: any;
}

const Button = ({
  narrow,
  extraWide,
  label,
  labelT,
  type,
  onClick,
  variant = 'default',
  tooltip,
  focus,
  ...props
}: Props & LabelOrLabelT) => {
  const { t } = useTranslation();

  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focus && null !== ref.current) {
      ref.current.focus();
    }
  }, [focus]);

  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  const getButtonColors = () => {
    // NOTE variant:
    // "outline-*" will replace "transparent"
    // "primary" will replace "warning"

    const isDarkBgColor = () => {
      return (
        variant === 'primary' || variant === 'danger' || variant === 'warning'
      );
    };

    const colorName = variant.startsWith('outline')
      ? variant.split('-')[1]
      : variant;
    const colorNameDark = colorName + 'Dark';

    const textColor =
      variant.startsWith('transparent') ||
        variant.startsWith('outline') ||
        variant.endsWith('text')
        ? colors.lobbyOrange
        : colors.white;
    const textColorDark =
      variant.startsWith('primary') && variant.endsWith('text')
        ? colors.lobbyOrangeDark
        : undefined;
    const borderColor =
      variant === 'default'
        ? colors.white
        : variant.startsWith('transparent') || variant.startsWith('outline')
          ? colors.lobbyOrange
          : // @ts-ignore
          (colors[colorName] as string);
    const borderColorDark =
      variant === 'default'
        ? colors.whiteDark
        : variant.startsWith('transparent') || variant.startsWith('outline')
          ? colors.lobbyOrangeDark
          : // @ts-ignore
          (colors[colorNameDark] as string);
    const bgColor =
      variant === 'default'
        ? colors.white
        : variant.startsWith('outline') || variant.endsWith('text')
          ? colors.transparent
          : // @ts-ignore
          (colors[colorName] as string);
    const bgColorDark =
      variant === 'default'
        ? colors.whiteDark
        : variant.startsWith('transparent') || variant.startsWith('outline')
          ? colors.lobbyOrangeDark
          : // @ts-ignore
          (colors[colorNameDark] as string);

    return {
      bgColorDark,
      bgColor,
      borderColorDark,
      borderColor,
      textColor,
      textColorDark,
      isDarkBgColor,
    };
  };

  const buttonLabel = labelT ? t(labelT.key, labelT.defaultValue) : label;
  const buttonContent = tooltip ? (
    <div
      css={css`
        padding-right: 27px;
      `}
    >
      {buttonLabel}
      <Tooltip title={tooltip} arrow>
        <img
          src={tooltipImage}
          alt={'tooltip-icon'}
          css={css`
            position: relative;
            height: 16px;
            margin-left: 10px;
          `}
        />
      </Tooltip>
    </div>
  ) : (
    buttonLabel
  );

  const buttonColors = getButtonColors();

  return !IS_EDU ? (
    <StyledButton2
      ref={ref}
      {...props}
      isEduLayout={IS_EDU}
      narrow={narrow}
      extraWide={extraWide}
      // @ts-ignore
      variant={variant}
      type={type}
      data-testid={labelT?.key}
      onClick={onClick}
    >
      {buttonContent}
    </StyledButton2>
  ) : (
    <StyledButton
      ref={ref}
      {...props}
      {...buttonColors}
      narrow={narrow}
      extraWide={extraWide}
      variant={variant}
      type={type}
      data-testid={labelT?.key}
      onClick={onClick}
    >
      {buttonContent}
    </StyledButton>
  );
};

export default Button;

interface StyledButtonProps2
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  narrow?: boolean;
  short?: boolean;
  extraWide?: boolean;
  variant:
  | 'default'
  | 'danger'
  | 'warning'
  | 'warningFaded'
  | 'transparent'
  | 'transparent70';
}

const StyledButton2 = styled('button', {
  shouldForwardProp: shouldForwardProp(),
}) <StyledButtonProps2>`
  border: ${({ variant }) =>
    variant.startsWith('transparent')
      ? `2px solid ${colors['warning']}`
      : 'none'};
  color: ${({ variant }) =>
    variant === 'warning'
      ? colors.white
      : variant.startsWith('transparent')
        ? colors.warning
        : colors.black};
  background: ${({ variant }) =>
    variant === 'default' ? colors.white : colors[variant]};
  text-align: center;
  padding: ${({ narrow, extraWide, short }) =>
    `${narrow ? '0.3em' : '1em'} ${extraWide ? '64px' : short ? '16px' : '32px'
    }`};
  font-family: inherit;
  font-weight: bold;
  font-size: 0.875em;
  letter-spacing: 0;
  border-radius: 6px;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: auto;
    opacity: 0.5;
  }

  ${layout}
  ${space}
`;

interface StyledButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  narrow?: boolean;
  short?: boolean;
  extraWide?: boolean;
  variant:
  | 'default'
  | 'primary'
  | 'primary-text'
  | 'danger'
  | 'warning'
  | 'warningFaded'
  | 'outline-default'
  | 'outline-primary'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-warningFaded'
  | 'transparent'
  | 'transparent70';
  bgColorDark: string;
  bgColor: string;
  borderColorDark: string;
  borderColor: string;
  textColor: string;
  textColorDark?: string;
  isDarkBgColor: () => boolean;
}

const StyledButton = styled('button', {
  shouldForwardProp: shouldForwardProp(),
}) <StyledButtonProps>`
  border: ${({ variant, borderColor }) =>
    variant.endsWith('text') ? '0' : `2px solid ${borderColor}`};
  color: ${({ textColor }) => textColor};
  background: ${({ bgColor }) => bgColor};
  text-align: center;
  padding: ${({ narrow, extraWide, short }) =>
    `${narrow ? '0.3em' : '.8em'} ${extraWide ? '64px' : short ? '16px' : '32px'
    }`};
  font-family: inherit;
  font-weight: bold;
  font-size: 1em;
  letter-spacing: 0;
  border-radius: 6px;

  &:hover:enabled {
    background: ${({ bgColorDark }) => bgColorDark};
    border-color: ${({ borderColorDark }) => borderColorDark};
    color: ${({ variant, isDarkBgColor, textColor, textColorDark }) =>
    `${variant.startsWith('transparent') ||
      variant.startsWith('outline') ||
      isDarkBgColor()
      ? colors.white
      : variant.endsWith('text')
        ? textColorDark
        : textColor
    }`};
  }

  &:disabled {
    cursor: auto;
    opacity: 0.5;
  }

  ${layout}
  ${space}
`;
